import React, { useState, useEffect, useMemo } from 'react';
import api from '../lib/api';
import toast from 'react-hot-toast';
import { UserCog, Plus, Edit2, X, Search, Cloud, Trash2, Ban, UserCheck } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const rotuloFuncionarioM8 = (id, nomesPorId, razaoPorId) => {
  if (id == null || id === '') return '—';
  const razao = razaoPorId?.[id];
  const nome = nomesPorId[id] || razao;
  const rotulo = razao || nome;
  return rotulo ? `${id} — ${rotulo}` : `ID ${id}`;
};

const usuarioAtivo = (u) => u?.ativo !== false;

const Usuarios = () => {
  const { user: usuarioLogado } = useAuth();
  const [users, setUsers] = useState([]);
  const [funcoes, setFuncoes] = useState([]);
  const [destinosM8, setDestinosM8] = useState([]);
  const [funcionariosM8, setFuncionariosM8] = useState([]);
  const [loadingFuncionarios, setLoadingFuncionarios] = useState(false);
  const [buscaM8, setBuscaM8] = useState('');
  const [loading, setLoading] = useState(true);
  const [acaoId, setAcaoId] = useState(null);

  const rotuloDestinoM8 = (id) => {
    if (id == null || id === '') return '—';
    const destino = destinosM8.find((d) => Number(d.id) === Number(id));
    return destino ? `${id} — ${destino.nome}` : `ID ${id}`;
  };
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    funcao_id: '',
    m8_tecnico_id: '',
    m8_destino_estoque_id: '',
  });
  const [newPassword, setNewPassword] = useState('');

  const nomesM8PorId = useMemo(() => {
    const map = {};
    funcionariosM8.forEach((f) => {
      map[f.funcionario_id] = f.razao_social || f.nome;
    });
    return map;
  }, [funcionariosM8]);

  const razaoM8PorId = useMemo(() => {
    const map = {};
    funcionariosM8.forEach((f) => {
      if (f.razao_social) map[f.funcionario_id] = f.razao_social;
    });
    return map;
  }, [funcionariosM8]);

  const funcionariosFiltrados = useMemo(() => {
    const termo = buscaM8.trim().toLowerCase();
    if (!termo) return funcionariosM8;
    return funcionariosM8.filter(
      (f) =>
        String(f.funcionario_id).includes(termo) ||
        (f.nome || '').toLowerCase().includes(termo) ||
        (f.razao_social || '').toLowerCase().includes(termo),
    );
  }, [funcionariosM8, buscaM8]);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      setLoading(true);
      const [usersRes, funcoesRes, configRes] = await Promise.all([
        api.get('/users'),
        api.get('/funcoes'),
        api.get('/config'),
      ]);
      setUsers(usersRes.data?.users || []);
      setFuncoes(funcoesRes.data?.funcoes || []);
      setDestinosM8(configRes.data?.m8_destinos_estoque || []);
    } catch (err) {
      toast.error('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
    carregarFuncionariosM8();
  };

  const carregarFuncionariosM8 = async () => {
    try {
      setLoadingFuncionarios(true);
      const res = await api.get('/m8/funcionarios');
      setFuncionariosM8(res.data?.funcionarios || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao carregar técnicos do M8');
      setFuncionariosM8([]);
    } finally {
      setLoadingFuncionarios(false);
    }
  };

  const isTecnico = (u) => {
    const slug = u.funcao?.slug || u.role || '';
    return slug === 'tecnico';
  };

  const handleToggleAtivo = async (u) => {
    if (u.id === usuarioLogado?.id) {
      toast.error('Não é possível inabilitar o próprio usuário');
      return;
    }
    const ativo = usuarioAtivo(u);
    const acao = ativo ? 'inabilitar' : 'reativar';
    const msg = ativo
      ? `Inabilitar ${u.name}? Esta pessoa não poderá mais entrar no sistema até ser reativada.`
      : `Reativar ${u.name}?`;
    if (!window.confirm(msg)) return;
    try {
      setAcaoId(u.id);
      await api.patch(`/users/${u.id}`, { ativo: !ativo });
      toast.success(ativo ? 'Usuário inabilitado' : 'Usuário reativado');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Erro ao ${acao} usuário`);
    } finally {
      setAcaoId(null);
    }
  };

  const handleDelete = async (u) => {
    if (u.id === usuarioLogado?.id) {
      toast.error('Não é possível excluir o próprio usuário');
      return;
    }
    if (!window.confirm(`Excluir ${u.name}? Esta ação não pode ser desfeita.`)) return;
    try {
      setAcaoId(u.id);
      await api.delete(`/users/${u.id}`);
      toast.success('Usuário excluído');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao excluir usuário');
    } finally {
      setAcaoId(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        const body = {
          name: formData.name,
          funcao_id: formData.funcao_id || null,
        };
        if (formIsTecnico) {
          body.m8_tecnico_id = formData.m8_tecnico_id ? Number(formData.m8_tecnico_id) : null;
          body.m8_destino_estoque_id = formData.m8_destino_estoque_id
            ? Number(formData.m8_destino_estoque_id)
            : null;
        }
        if (newPassword.trim()) body.new_password = newPassword;
        await api.patch(`/users/${editingUser.id}`, body);
        toast.success('Usuário atualizado!');
      } else {
        if (!formData.password.trim()) {
          toast.error('Informe a senha para novo usuário');
          return;
        }
        await api.post('/users', {
          email: formData.email,
          password: formData.password,
          name: formData.name,
          funcao_id: formData.funcao_id || null,
          m8_tecnico_id: formIsTecnico && formData.m8_tecnico_id
            ? Number(formData.m8_tecnico_id)
            : null,
          m8_destino_estoque_id: formIsTecnico && formData.m8_destino_estoque_id
            ? Number(formData.m8_destino_estoque_id)
            : null,
        });
        toast.success('Usuário criado!');
      }
      setShowModal(false);
      resetForm();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar usuário');
    }
  };

  const resetForm = () => {
    setEditingUser(null);
    setFormData({
      email: '',
      password: '',
      name: '',
      funcao_id: '',
      m8_tecnico_id: '',
      m8_destino_estoque_id: '',
    });
    setNewPassword('');
    setBuscaM8('');
  };

  const openCreate = () => {
    resetForm();
    setShowModal(true);
  };

  const openEdit = (u) => {
    setEditingUser(u);
    setFormData({
      email: u.email,
      password: '',
      name: u.name,
      funcao_id: u.funcao_id || u.funcao?.id || '',
      m8_tecnico_id: u.m8_tecnico_id != null ? String(u.m8_tecnico_id) : '',
      m8_destino_estoque_id: u.m8_destino_estoque_id != null ? String(u.m8_destino_estoque_id) : '',
    });
    setNewPassword('');
    setBuscaM8('');
    setShowModal(true);
  };

  const editingIsTecnico = editingUser ? isTecnico(editingUser) : false;
  const selectedFuncao = funcoes.find((f) => f.id === formData.funcao_id);
  const formIsTecnico = selectedFuncao?.slug === 'tecnico' || editingIsTecnico;

  const tecnicoSelecionadoRotulo = formData.m8_tecnico_id
    ? rotuloFuncionarioM8(Number(formData.m8_tecnico_id), nomesM8PorId, razaoM8PorId)
    : null;

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
            <UserCog className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Usuários do sistema</h1>
            <p className="text-slate-600">Acesso ao mobile e ao painel (técnico, administrativo, gerência)</p>
          </div>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white px-6 py-3 rounded-xl font-medium hover:shadow-lg transition-all flex items-center gap-2"
        >
          <Plus className="w-5 h-5" /> Novo usuário
        </button>
      </div>

      <div className="bg-white rounded-2xl shadow-sm overflow-hidden overflow-x-auto">
        <table className="w-full min-w-[800px]">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left py-4 px-6 text-sm font-medium text-slate-600">Nome (VFROTAS)</th>
              <th className="text-left py-4 px-6 text-sm font-medium text-slate-600">Email</th>
              <th className="text-left py-4 px-6 text-sm font-medium text-slate-600">Função</th>
              <th className="text-left py-4 px-6 text-sm font-medium text-slate-600">Status</th>
              <th className="text-left py-4 px-6 text-sm font-medium text-slate-600">Técnico no M8</th>
              <th className="text-left py-4 px-6 text-sm font-medium text-slate-600">Destino estoque M8</th>
              <th className="text-right py-4 px-6 text-sm font-medium text-slate-600">Ações</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr
                key={u.id}
                className={`border-b border-slate-100 hover:bg-slate-50 transition-colors ${usuarioAtivo(u) ? '' : 'opacity-60'}`}
              >
                <td className="py-4 px-6 text-sm font-medium text-slate-900">{u.name}</td>
                <td className="py-4 px-6 text-sm text-slate-600">{u.email}</td>
                <td className="py-4 px-6">
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
                    {u.funcao?.nome || u.role || '—'}
                  </span>
                </td>
                <td className="py-4 px-6">
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-medium ${
                      usuarioAtivo(u) ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'
                    }`}
                  >
                    {usuarioAtivo(u) ? 'Ativo' : 'Inabilitado'}
                  </span>
                </td>
                <td className="py-4 px-6 text-sm text-slate-600 max-w-xs">
                  {isTecnico(u) ? (
                    u.m8_tecnico_id ? (
                      <span className="block truncate" title={rotuloFuncionarioM8(u.m8_tecnico_id, nomesM8PorId, razaoM8PorId)}>
                        {rotuloFuncionarioM8(u.m8_tecnico_id, nomesM8PorId, razaoM8PorId)}
                      </span>
                    ) : (
                      <span className="text-amber-600 font-medium">Não vinculado</span>
                    )
                  ) : (
                    '—'
                  )}
                </td>
                <td className="py-4 px-6 text-sm text-slate-600">
                  {isTecnico(u) ? (
                    u.m8_destino_estoque_id ? (
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">
                        <Cloud className="w-3 h-3 inline mr-1" />
                        {rotuloDestinoM8(u.m8_destino_estoque_id)}
                      </span>
                    ) : (
                      <span className="text-amber-600 font-medium">Não configurado</span>
                    )
                  ) : (
                    '—'
                  )}
                </td>
                <td className="py-4 px-6 text-right whitespace-nowrap">
                  <button
                    type="button"
                    onClick={() => openEdit(u)}
                    className="text-blue-600 hover:text-blue-700 p-2 rounded-lg hover:bg-blue-50 transition-colors"
                    title="Editar"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  {u.id !== usuarioLogado?.id && (
                    <>
                      <button
                        type="button"
                        onClick={() => handleToggleAtivo(u)}
                        disabled={acaoId === u.id}
                        className={`p-2 rounded-lg transition-colors disabled:opacity-50 ${
                          usuarioAtivo(u)
                            ? 'text-amber-600 hover:text-amber-700 hover:bg-amber-50'
                            : 'text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50'
                        }`}
                        title={usuarioAtivo(u) ? 'Inabilitar' : 'Reativar'}
                      >
                        {usuarioAtivo(u) ? <Ban className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(u)}
                        disabled={acaoId === u.id}
                        className="text-red-600 hover:text-red-700 p-2 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50"
                        title="Excluir"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {users.length === 0 && (
          <div className="text-center py-12 text-slate-500">Nenhum usuário cadastrado. Crie um para acessar o mobile ou o painel.</div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-slate-900">
                {editingUser ? 'Editar usuário' : 'Novo usuário'}
              </h2>
              <button
                type="button"
                onClick={() => { setShowModal(false); resetForm(); }}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Nome</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none disabled:bg-slate-50"
                  required
                  disabled={!!editingUser}
                />
                {editingUser && <p className="text-xs text-slate-500 mt-1">Email não pode ser alterado.</p>}
              </div>
              {!editingUser && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Senha</label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none"
                    placeholder="Mínimo 6 caracteres"
                  />
                </div>
              )}
              {editingUser && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Nova senha (deixe em branco para não alterar)</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none"
                    placeholder="Opcional"
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Função (perfil de acesso)</label>
                <select
                  value={formData.funcao_id}
                  onChange={(e) => {
                    const funcao_id = e.target.value;
                    setFormData({
                      ...formData,
                      funcao_id,
                      m8_tecnico_id: '',
                      m8_destino_estoque_id: '',
                    });
                    const f = funcoes.find((x) => x.id === funcao_id);
                    if (f?.slug === 'tecnico' && funcionariosM8.length === 0) {
                      carregarFuncionariosM8();
                    }
                  }}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none"
                >
                  <option value="">— Selecione a função —</option>
                  {funcoes.map((f) => (
                    <option key={f.id} value={f.id}>{f.nome}</option>
                  ))}
                </select>
                <p className="text-xs text-slate-500 mt-1">Quem tem função &quot;Técnico&quot; usa o app mobile e aparece nas rotas.</p>
              </div>
              {formIsTecnico && (
                <div className="rounded-xl border border-indigo-100 bg-indigo-50/50 p-4 space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-slate-800 mb-1">
                      Quem é este técnico no M8?
                    </label>
                    <p className="text-xs text-slate-600">
                      Escolha a mesma pessoa cadastrada no ERP. Formato: <strong>ID — Nome</strong>
                    </p>
                  </div>

                  {loadingFuncionarios ? (
                    <div className="flex items-center gap-2 text-sm text-slate-600 py-2">
                      <div className="w-4 h-4 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                      Carregando lista do M8...
                    </div>
                  ) : funcionariosM8.length === 0 ? (
                    <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                      Não foi possível carregar os técnicos do M8.
                      <button
                        type="button"
                        onClick={carregarFuncionariosM8}
                        className="block mt-2 text-indigo-600 font-medium hover:underline"
                      >
                        Tentar novamente
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <input
                          type="text"
                          value={buscaM8}
                          onChange={(e) => setBuscaM8(e.target.value)}
                          placeholder="Buscar por nome ou ID..."
                          className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none text-sm"
                        />
                      </div>
                      <select
                        value={formData.m8_tecnico_id}
                        onChange={(e) => setFormData({ ...formData, m8_tecnico_id: e.target.value })}
                        size={Math.min(8, Math.max(4, funcionariosFiltrados.length + 1))}
                        className="w-full px-3 py-2 rounded-xl border border-slate-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none text-sm font-mono"
                      >
                        <option value="">— Selecione o técnico no M8 —</option>
                        {funcionariosFiltrados.map((f) => {
                          const rotulo = f.razao_social || f.nome;
                          return (
                            <option key={f.funcionario_id} value={String(f.funcionario_id)}>
                              {f.funcionario_id} — {rotulo}
                            </option>
                          );
                        })}
                      </select>
                      {funcionariosFiltrados.length === 0 && buscaM8.trim() && (
                        <p className="text-xs text-slate-500">Nenhum técnico encontrado para &quot;{buscaM8}&quot;</p>
                      )}
                      <p className="text-xs text-slate-500">
                        {funcionariosM8.length} técnicos no M8
                        {buscaM8.trim() ? ` · ${funcionariosFiltrados.length} na busca` : ''}
                      </p>
                    </>
                  )}

                  {tecnicoSelecionadoRotulo && (
                    <div className="text-sm bg-white border border-emerald-200 rounded-lg px-3 py-2 text-emerald-800">
                      Selecionado: <strong>{tecnicoSelecionadoRotulo}</strong>
                    </div>
                  )}

                  <div className="pt-2 border-t border-indigo-100">
                    <label className="block text-sm font-medium text-slate-800 mb-1">
                      <Cloud className="w-4 h-4 inline mr-1" />
                      Destino de estoque M8
                    </label>
                    <p className="text-xs text-slate-600 mb-2">
                      Estoque deste técnico no ERP (sincronização e baixa de peças).
                    </p>
                    <select
                      value={formData.m8_destino_estoque_id}
                      onChange={(e) => setFormData({ ...formData, m8_destino_estoque_id: e.target.value })}
                      className="w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none text-sm"
                    >
                      <option value="">— Selecione o destino —</option>
                      {destinosM8.map((destino) => (
                        <option key={destino.id} value={String(destino.id)}>
                          {destino.id} — {destino.nome}
                        </option>
                      ))}
                    </select>
                    {destinosM8.length === 0 && (
                      <p className="text-xs text-amber-600 mt-2">
                        Nenhum destino cadastrado. Adicione em Configurações → Destinos de estoque M8.
                      </p>
                    )}
                  </div>
                </div>
              )}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); resetForm(); }}
                  className="flex-1 px-4 py-3 rounded-xl border border-slate-200 text-slate-600 font-medium hover:bg-slate-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-medium hover:shadow-lg transition-all"
                >
                  {editingUser ? 'Salvar' : 'Criar usuário'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Usuarios;
